
import BannerSection from '@/components/homepage/BannerSection.vue'
import { defineComponent } from 'vue'
import { mapGetters, mapState } from 'vuex'
import ResizeObserver from '@/components/ResizeObserver.vue'
import TutorialModal from '@/components/TutorialModal.vue'
import MessageModal from '@/components/common/MessageModal.vue'
import UserToolbar from '@/components/UserToolbar.vue'
import { AuthActionTypes } from '@/store/modules/auth/interfaces/action-types'
import { LinksActionType, LinksMutationType } from '@/store/modules/links/interfaces'
import { setI18nLanguage } from '@/i18n'
import Error401Page from './pages/Error401Page.vue'
import BeforeLoginLayout from '@/components/layout/BeforeLoginLayout.vue'
import { LanguagesType } from '@/types'
import { HelpPagesActionType } from '@/store/modules/help-pages/interfaces'
import AppGlobalModal from '@/components/app/AppGlobalModal.vue'
import ModalComponent from './components/ModalComponent.vue'
import { AuthMutationTypes } from '@/store/modules/auth/interfaces/mutation-types'
import { RolePermissionDetails } from './store/modules/users/interfaces/IUser'
import { IAuthState } from './store/modules/auth/IAuthState'
import { IFetchSearchResponse } from '@/store/modules/main/intefaces/fetchSearch/IFetchSearchResponse'
import { IPortalHardCodeLink } from './store/modules/links/interfaces/IPortalHardCodeLink'
import { getValueByLabel } from './utils/permissions'
import AppLayout from '@/components/layout/AppLayout.vue'
import { getUnseenHelpPages } from './utils/newCapabilities'
import AlertModal from './components/AlertModal.vue'
import refreshAfterChangeLanguage from '@/mixins/refreshAfterChangeLanguage'

export default defineComponent({
  name: 'App',
  mixins: [refreshAfterChangeLanguage],
  components: {
    TutorialModal,
    Error401Page,
    MessageModal,
    UserToolbar,
    BeforeLoginLayout,
    ModalComponent,
    AppGlobalModal,
    ResizeObserver,
    AppLayout,
    BannerSection,
    AlertModal
  },
  data: () => ({
    showTutorialModal: false,
    gigICode: '',
    gigIvToken: '',
    isPortalChrome: true,
    isLoaded: false,
    openModal: false,
    message: '',
    subMessage: '',
    searchValue: '',
    searchValues: [] as IFetchSearchResponse[],
    isErrorModal: true,
    activeLanguage: (localStorage.getItem('lang')) as LanguagesType,
    clientWidth: 0,
    timeoutId: 0,
    content: {} as any,
    showAlert: false
  }),

  computed: {
    ...mapState('main', ['pageNotFound']),
    ...mapState('basket', ['id', 'items', 'progressBarMsg']),
    ...mapGetters('main', ['getPermissions']),
    portalHardCodeLink (): IPortalHardCodeLink[] {
      return this.$store.state.links.portalHardCodeLink?.results ?? []
    },
    total () {
      return this.$store.state.links.portalHardCodeLink.total
    },
    portalPageMenuLink () {
      return this.$store.state.links.portalPageMenuLink?.items ?? []
    },
    limit () {
      return this.$store.state.links.portalHardCodeLink.limit
    },
    queries () {
      return this.$store.state.catalog.queries
    },
    catalogPermission () {
      return this.checkPermission('Catalogue', {}).length !== 0
    },

    languages () {
      return ['it', 'en']
    },

    user () {
      return this.$store.state.auth.user
    },

    isLoggedIn () {
      return this.user.isLoggedIn // && !!this.user.userValidation
    },
    notAuthorized () {
      return this.$route.meta?.requireAuthentication && !this.isLoggedIn
    },
    isPOSUser () {
      return this.user?.permissionSchema?.some((company) => company.rpDetails.some((rp) => rp.customerType === 'POS'))
    },
    isWHSLUser () {
      return this.user?.permissionSchema?.some((company) => company.rpDetails?.some((rp) => rp.customerType === 'WHSL'))
    },
    isOutletUser () {
      return this.user?.permissionSchema?.some((company) =>
        company.rpDetails?.some((rp) => rp.customerType === 'Outlets')
      )
    },
    allPermission () {
      return this.user.allAvailablePermission
    },
    helpPagesStore () {
      return this.$store.state.helpPages
    },
    customerType (): RolePermissionDetails | undefined {
      if (this.user && this.user?.permissionSchema?.length > 0) {
        return this.user?.permissionSchema[0]?.rpDetails?.find((item: RolePermissionDetails) => item.customerType)
      }
      return undefined
    },
    isPOSAcademyUser () {
      return this.user.permissionSchema?.every(permission => permission.rpDetails.every(
        roleItem =>
          roleItem.rolename === 'POS Academy' ||
          roleItem.roleid === 'f5ab4859-9197-47e5-bc9e-097283c35d94'
      ))
    }
  },

  watch: {
    isLoggedIn: {
      handler (value) {
        if (value === true) {
          this.fetchPortalHardCodeLink()
          this.fetchPortalPageMenuLink()
          this.dispatchAlerts()
        }
      }
    },
    activeLanguage: {
      handler (value) {
        this.setMagentoStore(value)
        if (this.isLoggedIn) {
          this.fetchPortalHardCodeLink()
        }
      },
      immediate: true
    },
    customerType: {
      handler (newVal) {
        if (newVal) {
          this.fetchPortalPageMenuLink()
        }
      },
      immediate: true
    },

    $route () {
      this.initFromRoute()
      this.getAlertContent()
    },

    progressBarMsg (value, oldValue) {
      if (oldValue !== value) {
        this.$store.commit('basket/SET_PROGRESS_BAR', value)
        return value
      }
    }
  },

  created () {
    this.initFromRoute()
  },

  async mounted () {
    this.checkGigyaReady()
    const lang = (localStorage.getItem('lang') ?? 'it') as LanguagesType
    this.changeLanguage(lang)
    await this.dispatchAlerts()
    this.getAlertContent()
  },

  methods: {
    getAlertContent () {
      const availableAlert = this.$store.state.helpPages.alerts as any
      if (availableAlert?.length > 0) {
        const currentLocation = this.$route.name as string
        const alertForCurrentPage = availableAlert.find((item: any) => {
          const availablePages = item.portalSections?.map((section: any) => section.name) || []
          return availablePages.includes(currentLocation)
        })
        const storedAlerts = localStorage.getItem('alerts')
        const alreadyShownAlerts =
      storedAlerts && JSON.parse(storedAlerts || '[]')
        const alreadyShownForCurrentPage = alreadyShownAlerts?.find(
          (alert: { id: string; page: string; confirmationNeeded: boolean }) => {
            return alert.id === alertForCurrentPage?.id && alert.page === currentLocation
          }
        )
        if (alertForCurrentPage && !alreadyShownForCurrentPage) {
          this.content = alertForCurrentPage
          this.showAlert = true
          this.$gtm.trackEvent({ ecommerce: null })
          this.$gtm.trackEvent({
            event: 'view_promotion',
            promotion_id: this.content?.id,
            promotion_name: this.content?.name,
            page_location: window.location.href,
            page_path: currentLocation,
            event_timestamp: new Date().toISOString(),
            display_timestamp: new Date().toISOString(),
            display_sequence: this.content?.numberOfViews
          })
        } else {
          this.content = null
          this.showAlert = false
        }
      }
    },
    async dispatchAlerts () {
      const customertype = this.customerType?.customerType || localStorage.getItem('customertype') || ''
      const uid = sessionStorage.getItem('uid')
      await this.$store.dispatch(
        HelpPagesActionType.FETCH_ALERTS,
        {
          public: !this.isLoggedIn,
          customertype,
          uid
        }
      )
    },
    async refresh () {
      await this.dispatchAlerts()
    },

    checkGigyaReady () {
      if (window.gigya) {
        window.gigya.hasSession().then(this.checkSession)
        window.gigya.socialize.addEventHandlers({
          onLogin: async (res: IAuthState) => {
            await this.updateUserInfo(res)
            await this.$store.dispatch('basket/CREATE_EMPTY_CART', { root: true })
          }
        })
      } else {
        setTimeout(() => {
          this.checkGigyaReady()
        }, 100)
      }
    },

    initFromRoute () {
      this.isPortalChrome = !this.$route.meta?.noPortalChrome
      const urlParams = new URLSearchParams(window.location.search)
      this.gigICode = urlParams.get('gig_i_code') || ''
      this.gigIvToken = urlParams.get('gig_i_vToken') || ''
    },
    checkPermission (permission: string, value: Record<string, any>, additionalPremmision = true) {
      if (this.customerType) localStorage.setItem('customertype', this.customerType?.customerType)

      return this.allPermission?.includes(permission) &&
      additionalPremmision &&
      getValueByLabel(this.getPermissions(), permission)
        ? [value]
        : []
    },
    hasProductRecommendationPermission () {
      const hasPermission = this.user.permissionSchema?.some(permission =>
        permission.rpDetails.some(roleItem =>
          roleItem.permission === 'Product Recommendation'
        )
      )

      if (hasPermission) {
        localStorage.setItem('productrecoenabled', '1')
      }

      return hasPermission
    },
    async dispatchHelpPages () {
      const customerType = localStorage.getItem('customertype')
      const uid = sessionStorage.getItem('uid')

      await this.$store.dispatch(
        HelpPagesActionType.FETCH_ALL_HELP_PAGES,
        {
          CustomerType: customerType,
          uid
        }
      )
    },
    openTutorialModal () {
      this.showTutorialModal = true
    },
    closeTutorialModal (displayTime?: any) {
      this.showTutorialModal = false
      this.showAlert = false
      const currentLocation = this.$route.name as string
      this.$gtm.trackEvent({ ecommerce: null })
      this.$gtm.trackEvent({
        event: 'banner_dismissed',
        promotion_id: this.content?.id,
        promotion_name: this.content?.name,
        page_location: window.location.href,
        page_path: currentLocation,
        event_timestamp: new Date().toISOString(),
        display_sequence: this.content?.numberOfViews,
        display_duration_seconds: Math.floor((Date.now() - new Date(displayTime).getTime()) / 1000)
      })
    },
    changeLanguage (langCode: LanguagesType) {
      this.activeLanguage = langCode
      setI18nLanguage(langCode)
    },
    fetchPortalHardCodeLink () {
      const customertype = this.customerType?.customerType || localStorage.getItem('customertype')
      const uid = sessionStorage.getItem('uid')

      this.$store.dispatch(
        LinksActionType.FETCH_PORTAL_HARD_CODE_LINK,
        {
          customertype,
          uid
        }
      )
    },
    async fetchPortalPageMenuLink () {
      const customerType = this.customerType?.customerType
      const uid = sessionStorage.getItem('uid')

      if (customerType) {
        await this.$store.dispatch(
          LinksActionType.FETCH_PAGE_MENU_LINK,
          {
            CustomerType: customerType,
            uid
          }
        )
      } else {
        console.warn('customerType not available yet')
      }

      const isAcademyUser = this.user?.permissionSchema?.some((company) =>
        company.rpDetails?.some(
          (rp: any) => rp.permission.toLowerCase() === 'academy'
        )
      )

      if (!isAcademyUser) {
        let links = this.portalPageMenuLink as any[]

        links = links.filter((link: any) => {
          if (link.name.toLowerCase() === 'elearning') {
            return false
          } else {
            link.children = link?.children.filter(
              (child: any) => child.name.toLowerCase() !== 'elearning'
            )
            return true
          }
        })

        this.$store.commit(
          LinksMutationType.SET_PAGE_MENU_LINK,
          { items: links }
        )
      }
    },

    getLinkByName (name: string) {
      const linkObj = this.portalHardCodeLink.find((link) => link.name === name)
      return linkObj ? { url: linkObj.url, label: linkObj.label } : { url: '', label: '' }
    },

    /**
     * Method to update logged in user info into vuex store
     * @param res
     */
    async updateUserInfo (res: IAuthState) {
      try {
        sessionStorage.setItem('uid', res.UID) // This is for Google Analytics
        sessionStorage.setItem('getUserInfoResponse', JSON.stringify(res))
        this.$store.commit('SET_AUTH_USER', res)
        if (this.isLoggedIn) {
          this.setupTimers()
        }
        await this.$store.dispatch(AuthActionTypes.GET_USER_INFO, { id: res.UID }).then(() => {
          if (!this.user.userValidation && !this.$route.meta?.isUserVerification) {
            this.userLogout()
            return
          }

          this.hasProductRecommendationPermission()
          this.setMagentoStore(this.activeLanguage)
        })

        if (!!this.user.userValidation && !this.$route.meta?.isUserVerification) {
          await this.checkPOSAcademyUser()
        }

        if (
          !(this.helpPagesStore !== undefined && this.helpPagesStore.helpPages && this.helpPagesStore.helpPages.length)
        ) {
          await this.dispatchHelpPages()

          if ((this.user.firstLogin === true) ||
        (this.user.showTutorial > 0) ||
      (getUnseenHelpPages(this.helpPagesStore.helpPages.filter(h => h.newCapability === true), this.user.UID).length > 0)
          ) {
            this.openTutorialModal()
          }
        }
        if (localStorage.getItem('customertype') && localStorage.getItem('shippingMethod')) {
          await this.$store.dispatch('main/FETCH_ORDERABLE_UNITS_CONFIG',
            {
              customerType: localStorage.getItem('customertype'),
              orderType: localStorage.getItem('shippingMethod')
            }, { root: true })
        }
      } catch (error) {
        console.error('Error in updateUserInfo:', error)
      }
    },
    checkPOSAcademyUser () {
      if (this.isPOSAcademyUser) {
        window.gigya.fidm.saml.initSSO({
          spName: this.user.country === 'SVK' ? 'skELearning' : 'eLearning',
          redirectURL: this.user.country === 'SVK' ? this.getLinkByName('skELearning').url : this.getLinkByName('eLearning').url
        })
      }
    },
    /**
     * Method to initiate logout action
     * on click action of logout button
     */
    userLogout () {
      sessionStorage.removeItem('uid')
      sessionStorage.removeItem('imp_usr')
      sessionStorage.removeItem('isFromFavoritePage')
      const params = {
        UID: this.user.UID,
        callback: this.handleLogout
      }
      window.gigya.accounts.logout(params)
    },
    /**
     * Method to perform action after user logs out
     * Navigate user to home page
     */
    handleLogout () {
      this.$store.dispatch('logout')
      window.location.reload()
      this.$navigate('/login')
    },
    /**
     * Method to check user session if it exist
     * if it does not exist then redirect user to login page
     * @param sessionExist
     */
    async checkSession (sessionExist: boolean) {
      try {
        if (sessionExist) {
          window.gigya.socialize.getUserInfo({ callback: this.updateUserInfo })
        } else {
          if (!this.$route.meta?.isUserVerification && this.$route.meta?.requireAuthentication) {
            this.$navigate('/login')
          }
        }
      } catch (error) {
        console.error('Error in checkSession:', error)
      } finally {
        this.isLoaded = true
      }
    },

    setMagentoStore (language: string) {
      localStorage.setItem('store', `LivePortal_IT_${language}`)

      if (!(this.user.country == null || this.user.country === '') && !this.$store.state.auth.isLanguageLoaded) {
        this.$store.commit(AuthMutationTypes.SET_LANGUAGE_LOADED, true)
      }
    },

    onResizePortal ({ height }: { height: number }) {
      this.clientWidth = document.querySelector('body')?.clientWidth || height
    },
    startTimer () {
      this.timeoutId = setTimeout(this.userLogout, 900000)
    },
    resetTimer () {
      clearTimeout(this.timeoutId)
      this.startTimer()
    },
    setupTimers () {
      document.addEventListener('keypress', this.resetTimer, false)
      document.addEventListener('mousemove', this.resetTimer, false)
      document.addEventListener('mousedown', this.resetTimer, false)
      document.addEventListener('touchmove', this.resetTimer, false)
      this.startTimer()
    }
  }
})
